/**
 * useQuoteEditor — the single seam for opening / closing / syncing the
 * FullQuoteEditorModal (Stage 5).
 *
 * Background (the C2 candidate from `/improve-codebase-architecture` on
 * 2026-05-24): Stage 5 is a *virtual stage* — there is no Stage5*.jsx
 * page. `currentStage === 5` is true exactly when `quoteEditorOpen === true`
 * (see App.jsx:918). Before this hook landed, 13 sites across App.jsx
 * flipped `quoteEditorOpen` directly OR called the inline `openFinalQuoteEditor`
 * helper; only 6 of the 13 went through the helper, so the other 7 missed
 * its pre-merge step (Stage 4 scope edits would not ride into the modal).
 *
 * Vocabulary (per CONTEXT.md sharpening 2026-05-24):
 *   Stage 4 ↔ Stage 5 pair — two operator postures over one underlying
 *   Quote. Stage 4 = authoring (structure scope, edit per-line pricing);
 *   Stage 5 = verify-and-finalize (preview, final pricing edits,
 *   cost-calc mapping). Not sequential — both edit the same data.
 *
 * Four intents map to four named open methods. Each carries a different
 * merge / persistence contract because they semantically differ:
 *
 *   openAfterPipeline()  — generateQuote() just finished; quoteData is the
 *                          fresh, authoritative output. No per-call merge
 *                          (the generator already merged); no save here
 *                          (pipeline path calls saveCurrentProject with the
 *                          captureOriginalQuoteSnapshot flag separately).
 *
 *   openForNavigation()  — operator moved TO Stage 5 from elsewhere
 *                          (stage strip, requote, page nav, etc.). Live
 *                          `scope` may be ahead of `quoteData.scope` because
 *                          Stage 4 edits write `scope` directly — merge
 *                          required so the modal sees the latest authoring
 *                          edits. Saves if persistStage is requested.
 *
 *   peek()               — same-project view (a project load landing on
 *                          Stage 5, dashboard hover-quote button). Operator
 *                          is in a stage that hasn't touched scope, so no
 *                          merge is necessary. No save.
 *
 *   restore()            — project just hydrated from Dexie with
 *                          `currentStage:5`. Both containers are as-saved.
 *                          No merge, no save (saving back is no-op churn).
 *
 * Plus close() for the user-initiated close (syncs back, transitions to
 * Stage 4, persists), reset() for the hard reset path (sign-out, new
 * project, resetForm — does NOT sync back), and restoreFromSession() for
 * the sessionStorage hydration path that needs to set flags without firing
 * any side effects.
 *
 * Mutation contract: `setQuoteEditorOpen` should ONLY live inside this
 * hook after the migration. The grep-based lint guard at
 * `src/__tests__/quote-editor-funnel-guard.test.js` catches any new direct
 * callers in App.jsx.
 *
 * Q1 deferral note (gate-agnostic design): the C2 grilling left the
 * "should Stage 5 be gated behind Stage 4 completion?" question
 * provisional pending operator-behaviour observation. Adding a gate would
 * be a one-line early-return inside `openForNavigation` — the other three
 * intents would never gate by their nature. No structural change required
 * to add a gate later.
 *
 * Dependency-stability note (per DEC-COUNCIL-013, Kakashi F5): the `deps`
 * parameter is destructured ONCE per hook instance but the destructured
 * function references close over every `useCallback` in this file. For the
 * callbacks to remain stable across parent renders, the following deps
 * MUST be stable references (wrap in `useCallback` / `useMemo` in App.jsx):
 *   - setStageWithTransition
 *   - saveCurrentProject
 *   - effectiveCompletedStagesGetter
 * React's built-in `useState` setters (setScope, setQuoteData, setQuoteNote,
 * setCompletedStages) are guaranteed stable by React;
 * no wrapping needed.
 *
 * Peek vs restore semantic note (per DEC-COUNCIL-013, Hashirama F1):
 * `peek()` and `restore()` are exported as DIFFERENT named methods because
 * the call site's *semantic* intent differs (peek = "show me this quote
 * without changing state"; restore = "I just hydrated from Dexie, reopen
 * the modal at the saved point"). Both delegate to the same internal
 * `_openReadOnly()` helper so they cannot drift apart at the impl layer.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { scopeMergeKeys } from '../utils/quote-scope-merge';
import { ensureItemIds } from '../utils/item-id';
import { markQuoteRowsReviewed, scopeFingerprintForAutoDraft } from '../utils/auto-pipeline';
import { bindStage5PresentationToScope } from '../utils/project-stage';
import { mergeStage5EditsIntoStage4, sameRows, withLiveRowsMirrorArchive, withRowsMirrorContract } from '../utils/stage4-stage5-mirror';
import { fingerprintValue } from '../utils/quote-snapshot-diff';
import { reconcileQuoteHeroFromProjectSelection } from '../services/quote-hero-persistence';
import { resolveQuoteTitleSaveOverrides } from '../utils/quote-title-authority';
import { markQuoteJustGenerated } from '../services/assistant-dock';

function paintedLoadKey(contextRef) {
    const context = contextRef?.current;
    if (!context || typeof context !== 'object') return '';
    return String(context.projectId ?? '') + ':' + String(context.loadGeneration ?? '');
}

// The same load: equal, or a new project that got its id on its first save
// (":N" became "<id>:N" without a load).
function isSameOpenedLoad(opened, current) {
    if (opened === current) return true;
    return typeof opened === 'string' && opened.startsWith(':') && current.endsWith(opened) && current.length > opened.length;
}

export function useQuoteEditor(deps) {
    const {
        // State setters this hook drives
        setScope,
        setQuoteData,
        setSelectedHeroImage,
        setQuoteNote,
        // Optional — keeps App's quoteTitle in lock-step with in-editor title
        // edits when the modal syncs back (see syncBack). Passing these is what
        // stops a Stage 5 → 4 → 5 round-trip from reverting a title edit.
        setQuoteTitle,
        setTitleEditedByUser,
        setCompletedStages,
        setStageWithTransition,
        saveCurrentProject,
        // Live refs for "what's current right now" reads without
        // re-running the open* callbacks on every parent re-render
        stageRef,
        currentPageRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        paintedProjectLoadContextRef,
        projectAbortRef,
        scopeRef,
        quoteDataRef,
        selectedHeroImageRef,
        // Synchronous execution-lock probe. React's isGenerating state/ref can
        // lag the singleflight claim by a render, so it cannot protect the
        // pre-provider window from a stale Stage 5 navigation save.
        isQuoteGenerationInFlight,
        // True while a stored project's two copies of its items differ and
        // wait for the builder's choice (useStageRowsMirror). Stage 5 then
        // shows the quote's own copy and nothing is published into Stage 4.
        // quoteNoteRef — required for openForNavigation's note-precedence
        // contract (preserves pre-hook behaviour at App.jsx:10095, where
        // the live `quoteNote` state takes precedence over the stale value
        // embedded in `sourceQuoteData.note`).
        quoteNoteRef,
        // Current App-owned quote title. A populated modal title is not proof
        // of a builder edit: compare against this live value before claiming
        // titleEditedByUser authority during Stage 5 -> Stage 4 sync.
        quoteTitleRef,
        titleEditedByUserRef,
        // Optional getter that returns the parent's currently-effective
        // completedStages Set (App.jsx merges in restoredCompletedSeed +
        // inferred bumps, so we ask the parent rather than recompute).
        // Used only by openForNavigation when persistStage is true.
        effectiveCompletedStagesGetter,
    } = deps;

    // ───── State ─────────────────────────────────────────────────────────
    const [isOpen, setIsOpen] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    // isEditing default is FALSE: every open path explicitly sets isEditing,
    // and Stage 5's CONTEXT.md posture is "verify-and-finalize" — view-first.
    // (Per DEC-COUNCIL-013, Kakashi F2 — the pre-hook default of `true` at
    // App.jsx:1437 was vestigial dead-state-then-overwrite.)
    const [isEditing, setIsEditing] = useState(false);
    const [liveSnapshot, setLiveSnapshotState] = useState(null);
    // View-only Stage 5 reconciliation belongs to the modal presentation, not
    // the persisted App project. Legacy quotes may need scope binding or hero
    // reconciliation to render correctly; keeping that envelope here prevents
    // a pure Back/Forward click from dirtying quoteData/scope and arming autosave.
    const [presentationQuoteData, setPresentationQuoteDataState] = useState(null);
    const presentationQuoteDataRef = useRef(null);
    const setPresentationQuoteData = useCallback((next) => {
        presentationQuoteDataRef.current = next;
        setPresentationQuoteDataState(next);
    }, []);
    // Explicit Stage 5 Save returns the canonical post-upload project receipt.
    // Adopt that exact quote into both live state and the view-only presentation
    // shadow as one operation. Without this seam, App could save hero B while
    // the modal continued receiving the pre-navigation shadow containing A.
    const adoptCanonicalSave = useCallback((savedProject) => {
        const canonicalQuote = savedProject?.quoteData || savedProject?.quote_data || null;
        if (!canonicalQuote || typeof canonicalQuote !== 'object') return null;
        const canonicalScope = Array.isArray(savedProject?.scope)
            ? ensureItemIds(savedProject.scope)
            : (Array.isArray(canonicalQuote.scope) ? ensureItemIds(canonicalQuote.scope) : null);
        const quoteForState = canonicalScope
            ? bindStage5PresentationToScope({ ...canonicalQuote, scope: canonicalScope }, canonicalScope)
            : canonicalQuote;
        quoteDataRef.current = quoteForState;
        setQuoteData(quoteForState);
        if (canonicalScope) {
            scopeRef.current = canonicalScope;
            setScope(canonicalScope);
            // An accepted explicit Save re-grounds the open editor on the canonical rows (the editor presents this quote from
            // now on). Back's deletion and order authority needs the exact topology the editor STANDS on, so the baseline
            // moves with it; left at the topology it opened with, a delete or reorder made after a Save that already changed
            // the rows is judged against rows that no longer exist, and the removed rows come back. A row Stage 4 gains later
            // still differs from this baseline, which is what keeps it from being treated as a deletion.
            if (openRef.current === true) stage5TopologyBaseKeysRef.current = canonicalScope.map(scopeMergeKeys);
        }
        const hasSelectedHero = Object.prototype.hasOwnProperty.call(savedProject || {}, 'selectedHeroImage')
            || Object.prototype.hasOwnProperty.call(savedProject || {}, 'selected_hero_image');
        const canonicalSelectedHero = hasSelectedHero
            ? (savedProject.selectedHeroImage ?? savedProject.selected_hero_image ?? null)
            : (quoteForState?.header?.heroImage ?? null);
        if (selectedHeroImageRef) selectedHeroImageRef.current = canonicalSelectedHero;
        if (typeof setSelectedHeroImage === 'function') setSelectedHeroImage(canonicalSelectedHero);
        setPresentationQuoteData(quoteForState);
        lastPublishedNavigationPresentationRef.current = {
            projectId: savedProject?.id ?? currentProjectIdRef?.current ?? null,
            loadGeneration: projectLoadGenerationRef?.current ?? null,
            scopeFingerprint: fingerprintValue(canonicalScope ?? scopeRef.current ?? []),
            quoteFingerprint: fingerprintValue(quoteForState),
            durablyPersisted: true,
        };
        return quoteForState;
    }, [
        currentProjectIdRef,
        projectLoadGenerationRef,
        quoteDataRef,
        scopeRef,
        selectedHeroImageRef,
        setPresentationQuoteData,
        setQuoteData,
        setScope,
        setSelectedHeroImage,
    ]);
    // Stage 5 may paint immediately while its exact scope/media package is
    // still crossing the durability boundary. Keep that preview visible but
    // read-only until the canonical receipt lands, otherwise a fast editor
    // keystroke can be reset when portable media is written back by the save.
    const [presentationPersisting, setPresentationPersisting] = useState(false);

    // Live ref of isOpen for non-reactive callers (e.g. session-snapshot
    // writer at App.jsx:7044 reads quoteEditorOpenRef.current).
    const openRef = useRef(false);
    useEffect(() => { openRef.current = isOpen; }, [isOpen]);
    // The painted project load the editor opened on. Its rows are published
    // into Stage 4 only while it is open on that load, so a draft committed as
    // the editor unmounts during a project switch cannot land in the next
    // project (review M5, 2026-10-23).
    const openedForLoadRef = useRef(null);

    // liveSnapshot is exposed BOTH as state (for reactive consumers like
    // the nav-bar live-totals readout) AND as a ref (for event-handler
    // consumers that don't need a re-render every time the modal pushes a
    // keystroke). Per DEC-COUNCIL-013 Kakashi F1 — the modal at
    // FullQuoteEditorModal.jsx:3048-3051 fires onLiveSnapshotChange on
    // every change to data/customSignature/totals, so the cascade cost is
    // real. Setter `setLiveSnapshot` writes both at once.
    const liveSnapshotRef = useRef(null);
    const setLiveSnapshot = useCallback((next) => {
        liveSnapshotRef.current = next;
        setLiveSnapshotState(next);
    }, []);

    // Modal registers its action handlers here on mount (save, print,
    // email, syncBack, toggleReference). The hook reads `syncBack` during
    // close() and any nav-away path that needs to capture in-progress edits.
    const actionsRef = useRef({});
    // FullQuoteEditorModal is conditionally mounted by App while Stage 5 is
    // visible. Keep its resolved durable-hero display binding here, one level
    // above that mount boundary, so a view-only Stage 5 -> 4 -> 5 round-trip
    // does not fetch and recompress the same customer image again. The modal
    // still validates project/load context and exact source identity before
    // reuse; reset clears both refs on sign-out, project switch, or new project.
    const heroDisplayCacheRef = useRef({
        bindingRef: { current: null },
        contextKeyRef: { current: null },
    });
    const heroDisplayCache = heroDisplayCacheRef.current;
    // Exact row topology that was visible when the Stage 5 editor opened.
    // Back may apply user deletion/order only while live Stage 4 still has
    // this same topology; an unstamped concurrent WMS row must never vanish.
    const stage5TopologyBaseKeysRef = useRef(null);
    // NEXT, the Stage 5 pill and autonomous projection all share this one
    // project/load-bound transition. A second click joins the existing promise
    // instead of enqueuing another full project/media save.
    const navigationOpenSingleflightRef = useRef(null);
    // Last peer package published by attended Stage 4 -> 5 navigation. Keep it
    // across Back/Forward so reopening an unchanged quote does not republish
    // identical scope/quote objects (and thereby re-run modal media setup).
    // Project/load identity is part of the receipt, so another project can
    // never inherit this no-op decision.
    const lastPublishedNavigationPresentationRef = useRef(null);
    // A project/load key is deliberately stable across Stage 4↔5 clicks, but
    // the visible presentation is not. Back, close, reset, or an explicit
    // modal sync starts a new epoch so an unresolved prior save cannot own or
    // repaint a newly opened Stage 5 presentation.
    const presentationEpochRef = useRef(0);
    const invalidatePresentationEpoch = useCallback(() => {
        presentationEpochRef.current += 1;
        navigationOpenSingleflightRef.current = null;
        setPresentationPersisting(false);
    }, []);

    // ───── Internal helpers ──────────────────────────────────────────────

    /** Common open side-effects: set the modal open/view/edit flags.
     *  Every public open* method routes through this. */
    const _openInternal = useCallback(({
        viewMode: vm = true,
        isEditing: ie = false,
        topologyBaseScope = null,
    } = {}) => {
        const baseScope = Array.isArray(topologyBaseScope)
            ? topologyBaseScope
            : (Array.isArray(scopeRef?.current) ? scopeRef.current : []);
        stage5TopologyBaseKeysRef.current = baseScope.map(scopeMergeKeys);
        setViewMode(vm);
        setIsEditing(ie);
        // Non-reactive guards such as App's debounced costing refresh must see
        // Stage 5 ownership in the same event turn as the user's click. Waiting
        // for the passive isOpen effect leaves a window where duplicate work
        // can start while the modal is already opening.
        openRef.current = true;
        openedForLoadRef.current = paintedLoadKey(paintedProjectLoadContextRef);
        setIsOpen(true);
    }, [scopeRef, paintedProjectLoadContextRef]);

    /** The rows both stages hold after Stage 5's `rows`: a three-way merge
     *  against the rows the editor last took in from Stage 4 (the editor's own
     *  record when it has one) and Stage 4 as it is now. */
    const resolveStage4RowsFromStage5 = useCallback((rows) => {
        const liveScope = Array.isArray(scopeRef?.current) ? scopeRef.current : [];
        const absorbed = actionsRef.current?.getAbsorbedScope?.();
        const baseRows = Array.isArray(absorbed)
            ? absorbed
            : (presentationQuoteDataRef.current?.scope ?? quoteDataRef?.current?.scope ?? liveScope);
        return ensureItemIds(mergeStage5EditsIntoStage4({
            editedRows: rows,
            baseRows,
            currentRows: liveScope,
        }));
    }, [quoteDataRef, scopeRef]);

    /** A Stage 5 item edit, written into Stage 4 at once (Peter, 2026-10-23:
     *  "user should be able to manually or via ai assistant stage 4 and it
     *  should be instantly mirrored in stage 5. and vice versa"). The editor
     *  calls this for every change to its rows that it made itself, not for
     *  rows it took in from Stage 4. Nothing is published while a quote author
     *  owns the project. */
    const publishStage5Rows = useCallback((rows) => {
        if (!Array.isArray(rows)) return null;
        if (openRef.current !== true) return null;
        if (paintedProjectLoadContextRef && !isSameOpenedLoad(openedForLoadRef.current, paintedLoadKey(paintedProjectLoadContextRef))) return null;
        if (isQuoteGenerationInFlight?.() === true) return null;
        const liveScope = Array.isArray(scopeRef?.current) ? scopeRef.current : [];
        const merged = resolveStage4RowsFromStage5(rows);
        const liveQuote = quoteDataRef?.current;
        const scopeUnchanged = sameRows(merged, liveScope);
        const rowsForState = scopeUnchanged ? liveScope : merged;
        if (!scopeUnchanged) {
            scopeRef.current = rowsForState;
            setScope(rowsForState);
        }
        if (liveQuote && typeof liveQuote === 'object') {
            const nextQuote = markQuoteRowsReviewed(withRowsMirrorContract(liveQuote, rowsForState), rowsForState);
            if (nextQuote !== liveQuote) {
                quoteDataRef.current = nextQuote;
                setQuoteData(nextQuote);
            }
        }
        // The rows both stages hold now: the editor measures its next edit
        // from these.
        return rowsForState;
    }, [isQuoteGenerationInFlight, paintedProjectLoadContextRef, quoteDataRef, resolveStage4RowsFromStage5, scopeRef, setQuoteData, setScope]);

    /** Pull the modal's local `data` back up into App state. Returns the
     *  synced object so the caller can decide whether to persist. */
    const syncBack = useCallback(() => {
        // A quote already visible in Stage 5 can be the prior draft while its
        // replacement is running. Nav-away and close paths call syncBack, so
        // guarding only new editor opens is insufficient: stale modal-local
        // scope/title state must never become live Stage 4 authority mid-run.
        if (isQuoteGenerationInFlight?.() === true) return null;
        const modalContentEditReceipt = actionsRef.current?.getContentEditReceipt?.();
        const synced = actionsRef.current?.syncBack?.();
        if (synced && typeof synced === 'object') {
            invalidatePresentationEpoch();
            const liveQuote = quoteDataRef?.current;
            const liveScope = Array.isArray(scopeRef?.current) ? scopeRef.current : [];
            // The modal deliberately returns a fresh external-action envelope.
            // When its content is identical to the live quote, keep the existing
            // objects and publish nothing, but only while that quote remains
            // bound to the live Stage 4 scope. A background photo/WMS update
            // can make top-level scope newer while an unchanged modal is open.
            if (
                liveQuote
                && typeof liveQuote === 'object'
                && fingerprintValue(synced) === fingerprintValue(liveQuote)
                && fingerprintValue(liveQuote.scope ?? []) === fingerprintValue(liveScope)
            ) {
                return liveQuote;
            }
            // Modal-local Stage 5 data may predate a photo assignment that
            // completed against top-level scope while the preview was open.
            // Reconcile at the authority-aware peer seam so Back cannot erase
            // durable photo links; explicit Stage 5 edits with newer authority
            // stamps still win through the shared merge policy.
            // Stage 4 and Stage 5 are one set of items (stage4-stage5-mirror.ts).
            // Each Stage 5 edit already reached Stage 4 when it was made
            // (publishStage5Rows); this is the same three-way merge for anything
            // still in flight: every field Stage 5 changed since it last took in
            // Stage 4's rows wins, and every field only Stage 4 changed stays.
            const modalScope = Array.isArray(synced.scope) ? ensureItemIds(synced.scope) : null;
            const syncedScope = modalScope
                ? resolveStage4RowsFromStage5(modalScope)
                : null;
            const normalized = withLiveRowsMirrorArchive(syncedScope
                ? markQuoteRowsReviewed(
                    withRowsMirrorContract(
                        bindStage5PresentationToScope({ ...synced, scope: syncedScope }, syncedScope),
                        syncedScope,
                    ),
                    syncedScope,
                )
                : synced, liveQuote);
            const normalizedMatchesLive = !!liveQuote
                && typeof liveQuote === 'object'
                && fingerprintValue(normalized) === fingerprintValue(liveQuote);
            const quoteForState = normalizedMatchesLive ? liveQuote : normalized;
            if (!normalizedMatchesLive) {
                if (quoteDataRef) quoteDataRef.current = quoteForState;
                setQuoteData(quoteForState);
            }
            if (
                synced.note !== undefined
                && setQuoteNote
                && (quoteNoteRef?.current ?? '') !== synced.note
            ) {
                if (quoteNoteRef) quoteNoteRef.current = synced.note;
                setQuoteNote(synced.note);
            }
            if (syncedScope) {
                const scopeMatchesLive = fingerprintValue(syncedScope) === fingerprintValue(liveScope);
                if (!scopeMatchesLive) {
                    if (scopeRef) scopeRef.current = syncedScope;
                    setScope(syncedScope);
                }
            }
            // Keep App's quoteTitle aligned with an in-editor title edit. Without
            // this, a title changed inside the Stage 5 modal only landed in
            // quoteData.header.title; on reopen the modal hydrates from
            // projectTitle (= App's quoteTitle) FIRST, so a Stage 5 → 4 → 5
            // round-trip made the edit appear to revert. Mirrors the explicit-
            // Save sync in App.jsx (setQuoteTitle + mark edited-by-user).
            const syncedTitle = typeof synced.header?.title === 'string' ? synced.header.title.trim() : '';
            const currentTitle = String(
                quoteTitleRef?.current
                ?? liveQuote?.header?.title
                ?? '',
            ).trim();
            // A header/top-level mismatch can be legacy persisted state. Only
            // the modal's field-specific receipt proves a title edit in this
            // editor session; never infer title authority from the mismatch.
            const titleWasAuthored = modalContentEditReceipt?.titleChanged === true;
            if (titleWasAuthored && syncedTitle && syncedTitle !== currentTitle && setQuoteTitle) {
                if (quoteTitleRef) quoteTitleRef.current = syncedTitle;
                if (titleEditedByUserRef) titleEditedByUserRef.current = true;
                setQuoteTitle(syncedTitle);
                if (setTitleEditedByUser) setTitleEditedByUser(true);
            }
            return quoteForState;
        }
        return null;
    }, [setQuoteData, setScope, setQuoteNote, setQuoteTitle, setTitleEditedByUser, scopeRef, quoteDataRef, quoteNoteRef, quoteTitleRef, titleEditedByUserRef, isQuoteGenerationInFlight, invalidatePresentationEpoch, resolveStage4RowsFromStage5]);

    /** Snapshot Stage 5 for a page-level navigation. The real modal may add
     *  deterministic display defaults while mounting; its edit receipt is the
     *  authority for whether those bytes are authored. A no-edit leave returns
     *  the live App quote, rebound to any newer Stage 4 scope, without
     *  publishing modal setup into App state. */
    const syncForPageNavigation = useCallback(() => {
        if (isQuoteGenerationInFlight?.() === true) return null;
        const modalContentEditReceipt = actionsRef.current?.getContentEditReceipt?.();
        if (modalContentEditReceipt?.changed === false) {
            const liveQuote = quoteDataRef?.current;
            if (!liveQuote || typeof liveQuote !== 'object') return null;
            const liveScope = Array.isArray(scopeRef?.current) ? scopeRef.current : [];
            if (fingerprintValue(liveQuote.scope ?? []) === fingerprintValue(liveScope)) {
                return liveQuote;
            }
            return bindStage5PresentationToScope({ ...liveQuote, scope: liveScope }, liveScope);
        }
        return syncBack();
    }, [isQuoteGenerationInFlight, quoteDataRef, scopeRef, syncBack]);

    // ───── Open intents ──────────────────────────────────────────────────

    /** Pipeline-complete entry. generateQuote() finished and the side-effect
     *  payload requested editor-open. Optionally accepts the fresh payload
     *  so the contract is self-documenting (per DEC-COUNCIL-013, Hashirama
     *  F3 + Naruto Cond 1) — if `quoteContent` is passed, this method
     *  applies it via setQuoteData; if `quoteNote` is passed, it applies it
     *  via setQuoteNote. We DO NOT call saveCurrentProject here — the
     *  caller fires it separately with captureOriginalQuoteSnapshot so the
     *  payload-shape decision lives upstream. */
    const openAfterPipeline = useCallback(({ quoteContent, quoteNote } = {}) => {
        // The Quote Cockpit opens on its Assistant tab after a generation
        // (Peter, 2026-10-14), so the operator can ask for changes first.
        markQuoteJustGenerated();
        setPresentationQuoteData(null);
        if (quoteContent !== undefined) setQuoteData(quoteContent);
        if (quoteNote !== undefined && setQuoteNote) setQuoteNote(quoteNote);
        _openInternal({
            viewMode: true,
            isEditing: false,
            topologyBaseScope: Array.isArray(quoteContent?.scope)
                ? quoteContent.scope
                : scopeRef?.current,
        });
    }, [_openInternal, scopeRef, setPresentationQuoteData, setQuoteData, setQuoteNote]);

    /** Operator navigated TO Stage 5 from another stage. Merge live `scope`
     *  into quoteData.scope so the modal sees Stage 4's authoring edits. The
     *  exact package paints immediately and is saved by the same singleflight;
     *  a save/media outage must not hide a complete local builder-owned draft. */
    const openForNavigation = useCallback((sourceQuoteData, opts = {}) => {
        const callerWantsOutcome = opts?.returnOutcome === true;
        const operationPresentationEpoch = presentationEpochRef.current;
        const operationKey = JSON.stringify([
            currentProjectIdRef?.current ?? null,
            projectLoadGenerationRef?.current ?? null,
            opts?.transitionToStage5 !== false,
            opts?.persistStage === true,
            opts?.viewOnlyNavigation === true,
            opts?.persistenceReceipt?.scopeFingerprint ?? null,
            opts?.persistenceReceipt?.quoteFingerprint ?? null,
            operationPresentationEpoch,
        ]);
        const existing = navigationOpenSingleflightRef.current;
        if (existing?.key === operationKey && existing?.promise) {
            return existing.promise.then((outcome) => (
                callerWantsOutcome ? outcome : outcome?.opened === true
            ));
        }

        const operation = (async () => {
        const {
            transitionToStage5 = true,
            persistStage = false,
            // Stage preparation/completion owns durability. Once Stage 4/5
            // are available, clicking between their peer views must never
            // dispatch a project/media save.
            viewOnlyNavigation = false,
            persistenceReceipt = null,
            // Existing callers depend on the historical boolean return. The
            // autonomous Stage 5 projection opts into the richer receipt so it
            // cannot mistake an open-but-unsaved local draft for durable cloud
            // persistence.
        } = opts;
        const finish = ({ opened, persisted = null, persistenceError = null }) => ({
            opened,
            persisted,
            ...(persistenceError ? { persistenceError } : {}),
        });
        // The live ref wins over a caller snapshot. A quote author may finish
        // while an upstream photo gate is awaited; preferring an older explicit
        // object here would overwrite that newly saved commercial draft.
        const liveQuoteAtEntry = quoteDataRef?.current;
        const rawSource = (liveQuoteAtEntry
            && typeof liveQuoteAtEntry === 'object'
            && !Array.isArray(liveQuoteAtEntry)
            && Object.keys(liveQuoteAtEntry).length > 0)
            ? liveQuoteAtEntry
            : sourceQuoteData;
        const source = reconcileQuoteHeroFromProjectSelection(
            rawSource,
            selectedHeroImageRef?.current,
        );
        if (!source || typeof source !== 'object') {
            return finish({ opened: false });
        }
        // A prior Stage 5 draft may still be present while its replacement is
        // being authored. Open that exact peer read-only, but do not merge or
        // save it into the immutable Stage 4 snapshot owned by the active
        // author. FullQuoteEditorModal receives the same singleflight lock and
        // keeps edits/saves disabled while leaving outbound review available.
        if (isQuoteGenerationInFlight?.() === true) {
            // The draft on screen while its replacement is written still shows
            // the items both stages hold now, never an older copy (v6.0.114).
            const inFlightRows = Array.isArray(scopeRef?.current) ? scopeRef.current : null;
            setPresentationQuoteData(inFlightRows && source.scope !== inFlightRows
                ? { ...source, scope: inFlightRows }
                : source);
            _openInternal({ viewMode: true, isEditing: false });
            if (transitionToStage5 && stageRef?.current !== 5) {
                setStageWithTransition(5);
            }
            return finish({ opened: true });
        }
        const originStage = stageRef?.current;
        const originPage = currentPageRef?.current;
        const originProjectId = currentProjectIdRef?.current ?? null;
        const originLoadGeneration = projectLoadGenerationRef?.current ?? null;
        const originSignal = projectAbortRef?.current?.signal || null;
        // Keep the exact live array as the run owner. Stage 4 and persistence
        // repair scope immutably, including same-photo-ID inline -> durable
        // media replacement that the semantic fingerprint intentionally omits.
        // The O(1) identity check below therefore catches every replacement
        // without serialising multi-megabyte photo payloads on navigation.
        const originScopeRef = scopeRef?.current;
        const liveScopeAtEntry = Array.isArray(originScopeRef) ? originScopeRef : [];
        const originScopeFingerprint = scopeFingerprintForAutoDraft(liveScopeAtEntry);
        const originQuoteNote = quoteNoteRef?.current ?? null;
        const originQuoteDataFingerprint = fingerprintValue(liveQuoteAtEntry ?? null);
        const runContext = {
            projectId: originProjectId,
            loadGeneration: originLoadGeneration,
            signal: originSignal,
        };
        const navigationIntentIsCurrent = () => {
            const paintedContext = paintedProjectLoadContextRef?.current;
            const liveScope = Array.isArray(scopeRef?.current) ? scopeRef.current : [];
            return (
            stageRef?.current === originStage
            && (currentPageRef == null || currentPageRef.current === originPage)
            && (currentProjectIdRef == null || currentProjectIdRef.current === originProjectId)
            && (projectLoadGenerationRef == null || projectLoadGenerationRef.current === originLoadGeneration)
            && (projectAbortRef == null || projectAbortRef.current?.signal === originSignal)
            && !originSignal?.aborted
            && presentationEpochRef.current === operationPresentationEpoch
            && (paintedProjectLoadContextRef == null || (
                paintedContext?.projectId === originProjectId
                && Number(paintedContext?.loadGeneration) === Number(originLoadGeneration)
            ))
            && (scopeRef == null || scopeRef.current === originScopeRef)
            && scopeFingerprintForAutoDraft(liveScope) === originScopeFingerprint
            && (quoteNoteRef == null || (quoteNoteRef.current ?? null) === originQuoteNote)
            && (quoteDataRef == null || fingerprintValue(quoteDataRef.current ?? null) === originQuoteDataFingerprint)
            );
        };
        if (!navigationIntentIsCurrent()) return finish({ opened: false });
        const liveScope = liveScopeAtEntry;
        // Stage 4 and Stage 5 are one set of items: Stage 5 opens on the
        // items both stages hold. Neither copy leads (Peter, 2026-10-23).
        const normalizedScope = ensureItemIds(liveScope);
        // Per DEC-COUNCIL-013 Hashirama F2 — preserve the pre-hook
        // note-precedence at App.jsx:10095 (`note: quoteNote || source.note`).
        // Live `quoteNote` (Stage 4-edited) takes precedence over the stale
        // value embedded in `sourceQuoteData`.
        const liveNote = quoteNoteRef?.current;
        const updatedQuoteData = bindStage5PresentationToScope({
            ...source,
            scope: normalizedScope,
            note: (liveNote || source.note),
        }, normalizedScope);

        // Stage 4 and Stage 5 are peer views over this already-complete local
        // package. Paint synchronously; persistence is an independent durability
        // receipt and must not make a normal peer-view navigation look broken.
        // The modal remains preview-only until that receipt resolves so its
        // local edit buffer cannot be re-seeded over a just-entered edit by
        // canonical media write-back from the parent.
        setPresentationPersisting(true);
        const presentationPublicationReceipt = {
            projectId: originProjectId,
            loadGeneration: originLoadGeneration,
            scopeFingerprint: fingerprintValue(normalizedScope),
            quoteFingerprint: fingerprintValue(updatedQuoteData),
        };
        const priorPublication = lastPublishedNavigationPresentationRef.current;
        const presentationAlreadyPublished = priorPublication?.projectId === presentationPublicationReceipt.projectId
            && priorPublication?.loadGeneration === presentationPublicationReceipt.loadGeneration
            && priorPublication?.scopeFingerprint === presentationPublicationReceipt.scopeFingerprint
            && priorPublication?.quoteFingerprint === presentationPublicationReceipt.quoteFingerprint;
        const presentationAlreadyDurable = presentationAlreadyPublished
            && priorPublication?.durablyPersisted === true;
        const externallyProvedDurable = persistenceReceipt?.projectId === originProjectId
            && Number(persistenceReceipt?.loadGeneration) === Number(originLoadGeneration)
            && persistenceReceipt?.scopeFingerprint === presentationPublicationReceipt.scopeFingerprint
            && persistenceReceipt?.quoteFingerprint === presentationPublicationReceipt.quoteFingerprint;
        const exactPresentationIsDurable = presentationAlreadyDurable || externallyProvedDurable;
        if (viewOnlyNavigation) {
            if (fingerprintValue(presentationQuoteDataRef.current) !== fingerprintValue(updatedQuoteData)) {
                setPresentationQuoteData(updatedQuoteData);
            }
            _openInternal({ viewMode: true, isEditing: false, topologyBaseScope: normalizedScope });
            if (transitionToStage5 && stageRef?.current !== 5) {
                stageRef.current = 5;
                setStageWithTransition(5);
            }
            const persistenceStatus = exactPresentationIsDurable ? true : null;
            lastPublishedNavigationPresentationRef.current = {
                ...presentationPublicationReceipt,
                // Visibility is not a persistence receipt. Carry durability
                // only when this hook previously saved this exact package.
                durablyPersisted: exactPresentationIsDurable,
            };
            // Unknown is deliberately distinct from failure. Peer navigation
            // never attempted storage, so it cannot honestly return false or
            // raise the degraded-save warning merely because this hook does
            // not own the pipeline's earlier persistence receipt.
            return finish({ opened: true, persisted: persistenceStatus });
        }
        setPresentationQuoteData(null);
        if (!presentationAlreadyPublished) {
            scopeRef.current = normalizedScope;
            setScope(normalizedScope);
            quoteDataRef.current = updatedQuoteData;
            setQuoteData(updatedQuoteData);
            lastPublishedNavigationPresentationRef.current = {
                ...presentationPublicationReceipt,
                durablyPersisted: false,
            };
        }
        _openInternal({ viewMode: true, isEditing: false, topologyBaseScope: normalizedScope });
        if (transitionToStage5 && stageRef?.current !== 5) {
            stageRef.current = 5;
            setStageWithTransition(5);
        }
        // Once this exact peer package has a successful durability receipt,
        // Back/Forward is presentation only. Re-saving it would re-run media
        // portability and bump the project row despite no authored change.
        if (exactPresentationIsDurable) {
            return finish({ opened: true, persisted: true });
        }
        const presentationStage = transitionToStage5 ? 5 : originStage;
        const presentationScopeRef = normalizedScope;
        const presentationQuoteFingerprint = fingerprintValue(updatedQuoteData);
        const canonicalReceiptMatchesCurrentRefs = (savedProject) => {
            if (!savedProject || typeof savedProject !== 'object') return false;
            const hasCanonicalScope = Array.isArray(savedProject.scope);
            const canonicalQuote = savedProject.quoteData || savedProject.quote_data;
            const hasCanonicalQuote = !!canonicalQuote && typeof canonicalQuote === 'object';
            // A receipt must contribute at least one canonical payload. An
            // identity-only `{ id }` response cannot relax the pre-save owner.
            if (!hasCanonicalScope && !hasCanonicalQuote) return false;
            const liveScope = scopeRef?.current;
            const liveQuote = quoteDataRef?.current;
            const scopeMatches = hasCanonicalScope
                ? (liveScope === savedProject.scope
                    || fingerprintValue(liveScope ?? null) === fingerprintValue(savedProject.scope))
                : liveScope === presentationScopeRef;
            const quoteMatches = hasCanonicalQuote
                ? (liveQuote === canonicalQuote
                    || fingerprintValue(liveQuote ?? null) === fingerprintValue(canonicalQuote))
                : fingerprintValue(liveQuote ?? null) === presentationQuoteFingerprint;
            return scopeMatches && quoteMatches;
        };
        const presentationIntentIsCurrent = (canonicalSavedProject = null) => {
            const paintedContext = paintedProjectLoadContextRef?.current;
            const refsStillOwnPresentation = (
                (scopeRef == null || scopeRef.current === presentationScopeRef)
                && (quoteDataRef == null || fingerprintValue(quoteDataRef.current ?? null) === presentationQuoteFingerprint)
            );
            return (
                stageRef?.current === presentationStage
                && (currentPageRef == null || currentPageRef.current === originPage)
                && (currentProjectIdRef == null || currentProjectIdRef.current === originProjectId)
                && (projectLoadGenerationRef == null || projectLoadGenerationRef.current === originLoadGeneration)
                && (projectAbortRef == null || projectAbortRef.current?.signal === originSignal)
                && !originSignal?.aborted
                && presentationEpochRef.current === operationPresentationEpoch
                && (paintedProjectLoadContextRef == null || (
                paintedContext?.projectId === originProjectId
                && Number(paintedContext?.loadGeneration) === Number(originLoadGeneration)
            ))
                && (refsStillOwnPresentation
                    || canonicalReceiptMatchesCurrentRefs(canonicalSavedProject))
            );
        };
        let canonicalSavedProject = null;
        let persisted = false;
        let persistenceError = null;
        try {
            const currentCompleted = effectiveCompletedStagesGetter?.() || new Set();
            const savedProject = await saveCurrentProject({
                completedStages: Array.from(currentCompleted),
                quoteData: updatedQuoteData,
                scope: normalizedScope,
                ...(transitionToStage5 && persistStage ? { currentStage: 5 } : {}),
            }, {
                runContext,
                beforeCommit: () => {
                    if (presentationIntentIsCurrent()) return;
                    const error = new Error('Stage 5 navigation changed before the peer package could be committed.');
                    error.name = 'AbortError';
                    throw error;
                },
            });
            if (!savedProject) {
                throw new Error('The project save did not return a persistence receipt.');
            }
            if (!presentationIntentIsCurrent(savedProject)) {
                return finish({ opened: true, persisted: false });
            }
            canonicalSavedProject = savedProject;
            persisted = true;
        } catch (err) {
            if (!presentationIntentIsCurrent()) {
                return finish({ opened: true, persisted: false });
            }
            console.warn('[useQuoteEditor.openForNavigation] persist failed; opening the complete local Stage 5 draft:', err?.message || err);
            canonicalSavedProject = { quoteData: updatedQuoteData };
            persistenceError = String(err?.message || err || 'Project save failed');
        }
        const canonicalQuoteData = canonicalSavedProject.quoteData
            || canonicalSavedProject.quote_data
            || updatedQuoteData;
        const hasCanonicalSelectedHero = Object.prototype.hasOwnProperty.call(canonicalSavedProject, 'selectedHeroImage')
            || Object.prototype.hasOwnProperty.call(canonicalSavedProject, 'selected_hero_image');
        const canonicalSelectedHero = hasCanonicalSelectedHero
            ? (canonicalSavedProject.selectedHeroImage ?? canonicalSavedProject.selected_hero_image ?? null)
            : (canonicalQuoteData?.header?.heroImage ?? null);
        const canonicalScope = Array.isArray(canonicalSavedProject.scope)
            ? ensureItemIds(canonicalSavedProject.scope)
            : normalizedScope;
        if (scopeRef.current !== canonicalScope && fingerprintValue(scopeRef.current) !== fingerprintValue(canonicalScope)) {
            scopeRef.current = canonicalScope;
            setScope(canonicalScope);
        }
        if (fingerprintValue(quoteDataRef.current ?? null) !== fingerprintValue(canonicalQuoteData)) {
            quoteDataRef.current = canonicalQuoteData;
            setQuoteData(canonicalQuoteData);
        }
        if (
            typeof setSelectedHeroImage === 'function'
            && fingerprintValue(canonicalSelectedHero) !== fingerprintValue(updatedQuoteData?.header?.heroImage ?? null)
        ) {
            setSelectedHeroImage(canonicalSelectedHero);
        }
        lastPublishedNavigationPresentationRef.current = {
            projectId: originProjectId,
            loadGeneration: originLoadGeneration,
            scopeFingerprint: fingerprintValue(scopeRef.current ?? canonicalScope),
            quoteFingerprint: fingerprintValue(quoteDataRef.current ?? canonicalQuoteData),
            durablyPersisted: persisted === true,
        };
        return finish({ opened: true, persisted, persistenceError });
        })();
        const tracked = operation.finally(() => {
            if (navigationOpenSingleflightRef.current?.promise === tracked) {
                navigationOpenSingleflightRef.current = null;
                setPresentationPersisting(false);
            }
        });
        navigationOpenSingleflightRef.current = { key: operationKey, promise: tracked };
        return tracked.then((outcome) => (
            callerWantsOutcome ? outcome : outcome?.opened === true
        ));
    }, [
        _openInternal,
        scopeRef,
        stageRef,
        currentPageRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        paintedProjectLoadContextRef,
        projectAbortRef,
        quoteDataRef,
        selectedHeroImageRef,
        quoteNoteRef,
        setPresentationQuoteData,
        setScope,
        setQuoteData,
        setSelectedHeroImage,
        setStageWithTransition,
        effectiveCompletedStagesGetter,
        saveCurrentProject,
        isQuoteGenerationInFlight,
    ]);

    /**
     * Quick Edit entry. A quote recovered from a stored artefact or read off an
     * uploaded PDF, which did NOT come from the loaded project — and may have no
     * project at all, because an artefact deliberately outlives the project it
     * was produced under.
     *
     * WHY THIS IS NOT openForNavigation. That one merges the LIVE `scope` into
     * the quote it is handed, which is right when both describe the same job and
     * catastrophic here: the live scope belongs to whatever project happens to
     * be loaded, so merging would splice another job's line items into the
     * recovered quote. This method takes the recovered quote as authoritative
     * and drives `scope` FROM it, which is the opposite direction.
     *
     * THE CALLER MUST HAVE CLEARED THE PROJECT CONTEXT FIRST (App.jsx routes
     * through handleStartNewProject). With no currentProjectId a later save
     * mints a NEW project instead of overwriting the loaded one — which is also
     * how a quote that went missing from history gets a record again.
     *
     * Deliberately does not save. Nothing is persisted until the operator acts,
     * because a recovered quote on the extract path carries figures that have
     * not been confirmed yet (see isReadyToPrint).
     */
    const openRecovered = useCallback((recoveredQuoteData) => {
        if (!recoveredQuoteData || typeof recoveredQuoteData !== 'object') return false;
        setPresentationQuoteData(null);
        const recoveredScope = Array.isArray(recoveredQuoteData.scope)
            ? ensureItemIds(recoveredQuoteData.scope)
            : [];
        const normalized = { ...recoveredQuoteData, scope: recoveredScope };

        setScope(recoveredScope);
        setQuoteData(normalized);
        if (typeof normalized.note === 'string' && setQuoteNote) setQuoteNote(normalized.note);

        // Mark the title as user-owned so the save path's title generator does
        // not overwrite the recovered quote's own heading with one derived from
        // an address that may not have been recovered.
        const recoveredTitle = typeof normalized.header?.title === 'string'
            ? normalized.header.title.trim()
            : '';
        if (recoveredTitle && setQuoteTitle) {
            setQuoteTitle(recoveredTitle);
            if (setTitleEditedByUser) setTitleEditedByUser(true);
        }

        // Only the stages this quote genuinely carries. A recovered quote has a
        // scope and a finished quote; it has no risk analysis and no work method
        // statement, and claiming those were completed would put a tick against
        // work that was never done.
        if (setCompletedStages) setCompletedStages(new Set([1, 4, 5]));

        _openInternal({ viewMode: true, isEditing: false, topologyBaseScope: recoveredScope });
        if (stageRef?.current !== 5) setStageWithTransition(5);
        return true;
    }, [
        _openInternal,
        stageRef,
        setScope,
        setQuoteData,
        setQuoteNote,
        setQuoteTitle,
        setTitleEditedByUser,
        setCompletedStages,
        setStageWithTransition,
        setPresentationQuoteData,
    ]);

    /** Shared read-only opener used by both `peek` and `restore`. They are
     *  exported as separate named methods because the call-site SEMANTIC
     *  intent differs, but the impl is identical — and locking the impl
     *  in one place prevents the two from drifting apart (per DEC-COUNCIL-013,
     *  Hashirama F1). */
    const _openReadOnly = useCallback(() => {
        setPresentationQuoteData(null);
        _openInternal({ viewMode: true, isEditing: false });
    }, [_openInternal, setPresentationQuoteData]);

    /** Peek: open in view mode without modifying state. A project load that
     *  lands on Stage 5 and the dashboard hover-quote button use this —
     *  the operator hasn't touched scope so no merge is needed; no save
     *  because peek doesn't change state. The caller is responsible for
     *  ensuring `quoteData` is already in App state — peek does NOT take
     *  a payload (per DEC-COUNCIL-013 Kakashi F4 — closing the previous
     *  resolved-and-discarded-arg footgun). */
    const peek = useCallback(() => {
        _openReadOnly();
        return true;
    }, [_openReadOnly]);

    /** Restore: project just hydrated from Dexie at currentStage:5. Both
     *  containers were saved together so they already match — no merge,
     *  no save. Accepts an optional `{ expectedStage }` precondition; if
     *  passed and the live stageRef.current doesn't match, refuses + warns
     *  rather than opening on stale state (per DEC-COUNCIL-013 Hashirama
     *  F4 — guards against the load-coordination race fixed in v5.39.1). */
    const restore = useCallback(({ expectedStage } = {}) => {
        if (expectedStage !== undefined) {
            if (stageRef?.current !== expectedStage) {
                console.warn(
                    `[useQuoteEditor.restore] refused: expectedStage=${expectedStage} but stageRef.current=${stageRef?.current}`,
                );
                return false;
            }
            if (!quoteDataRef?.current) {
                console.warn('[useQuoteEditor.restore] refused: quoteDataRef.current is empty');
                return false;
            }
        }
        _openReadOnly();
        return true;
    }, [_openReadOnly, stageRef, quoteDataRef]);

    // ───── Close + reset ─────────────────────────────────────────────────

    /** User-initiated close. Sync the modal's edits back, transition to
     *  Stage 4 if we're currently rendered as Stage 5, persist. Returns
     *  the synced object so the caller can react to it (e.g. callers that
     *  want to know what was saved). */
    const close = useCallback((options = {}) => {
        const transitionToStage4 = options?.transitionToStage4 !== false;
        const quoteGenerationOwnsProject = isQuoteGenerationInFlight?.() === true;
        // Stage 5 navigation and its immediate Back action are one project-run
        // owner. `saveCurrentProject` otherwise defaults an option-less close
        // save to projectPersistenceAbortRef, while openForNavigation uses
        // projectAbortRef. If an unrelated save is already active, those two
        // signals meet in the pending queue as different owners and the newer
        // Back intent is rejected before it can replace the stale Stage 5
        // commit predicate. Capture the live navigation owner once and thread
        // it through either close-save branch so Back is always last-wins.
        const closeRunContext = {
            projectId: currentProjectIdRef?.current ?? null,
            loadGeneration: projectLoadGenerationRef?.current ?? null,
            signal: projectAbortRef?.current?.signal || null,
        };
        const quoteFingerprintBeforeSync = fingerprintValue(quoteDataRef?.current ?? null);
        const scopeFingerprintBeforeSync = fingerprintValue(scopeRef?.current ?? []);
        const modalContentEditReceipt = actionsRef.current?.getContentEditReceipt?.();
        const modalProvesNoAuthoredChange = modalContentEditReceipt?.changed === false;
        const epochBeforeSync = presentationEpochRef.current;
        // The real modal enriches sparse drafts with deterministic display
        // defaults. Its own normalized-open baseline can prove those bytes are
        // setup-only; do not sync that presentation envelope into App state.
        const synced = modalProvesNoAuthoredChange
            ? (quoteDataRef?.current ?? null)
            : syncBack();
        if (presentationEpochRef.current === epochBeforeSync) invalidatePresentationEpoch();
        // Stage 4 as syncBack left it: the merged rows when it merged, the
        // live rows while a version choice waits. The returned quote's rows
        // would save Stage 5's version as Stage 4 then (review round 7).
        const syncedScope = Array.isArray(scopeRef?.current)
            ? scopeRef.current
            : (Array.isArray(synced?.scope) ? synced.scope : null);
        const syncedQuoteFingerprint = fingerprintValue(quoteDataRef?.current ?? synced ?? null);
        const syncedScopeFingerprint = fingerprintValue(scopeRef?.current ?? syncedScope ?? []);
        const contentChanged = typeof modalContentEditReceipt?.changed === 'boolean'
            ? modalContentEditReceipt.changed
            : (syncedQuoteFingerprint !== quoteFingerprintBeforeSync
                || syncedScopeFingerprint !== scopeFingerprintBeforeSync);
        const titleSaveOverrides = resolveQuoteTitleSaveOverrides({
            quoteData: synced,
            currentQuoteTitle: quoteTitleRef?.current,
            currentTitleEditedByUser: titleEditedByUserRef?.current,
            titleChanged: modalContentEditReceipt?.titleChanged === true,
        });
        const persistSyncedEdit = (overrides, warningPrefix) => {
            const receipt = {
                projectId: closeRunContext.projectId,
                loadGeneration: closeRunContext.loadGeneration,
                scopeFingerprint: syncedScopeFingerprint,
                quoteFingerprint: syncedQuoteFingerprint,
                durablyPersisted: false,
            };
            lastPublishedNavigationPresentationRef.current = receipt;
            saveCurrentProject(overrides, {
                runContext: closeRunContext,
            }).then((savedProject) => {
                if (!savedProject) return;
                if (
                    currentProjectIdRef?.current === receipt.projectId
                    && projectLoadGenerationRef?.current === receipt.loadGeneration
                    && fingerprintValue(scopeRef?.current ?? []) === receipt.scopeFingerprint
                    && fingerprintValue(quoteDataRef?.current ?? null) === receipt.quoteFingerprint
                ) {
                    lastPublishedNavigationPresentationRef.current = {
                        ...receipt,
                        durablyPersisted: true,
                    };
                }
            }).catch((err) => {
                console.warn(`${warningPrefix} (continuing):`, err?.message);
            });
        };
        openRef.current = false;
        setIsOpen(false);
        setPresentationQuoteData(null);
        setViewMode(false);
        setIsEditing(false);
        if (stageRef?.current === 5) {
            if (transitionToStage4) {
                stageRef.current = 4;
                setStageWithTransition(4);
            }
            // Closing a prior quote preview is allowed, but it is a presentation
            // action only while the replacement author owns this project.
            // Persisting modal data or stage here would race the authored save.
            if (quoteGenerationOwnsProject) return null;
            if (contentChanged) {
                persistSyncedEdit({
                    ...(transitionToStage4 ? { currentStage: 4 } : {}),
                    ...(synced ? { quoteData: synced } : {}),
                    ...(syncedScope ? { scope: syncedScope } : {}),
                    ...titleSaveOverrides,
                }, '[useQuoteEditor.close] stage-5 edit persist failed');
            }
            return synced;
        }
        // Bare-save branch: stageRef.current !== 5 means a nav-away path
        // already moved the stage indicator before close() ran (e.g. stage
        // strip click that fires syncQuoteEditorBackToState then close).
        // We deliberately OMIT `currentStage` from this save payload so we
        // don't overwrite the nav-away path's chosen destination — that
        // path knows where it's going; close() shouldn't second-guess.
        if (synced && contentChanged) {
            persistSyncedEdit({
                quoteData: synced,
                ...(syncedScope ? { scope: syncedScope } : {}),
                ...titleSaveOverrides,
            }, '[useQuoteEditor.close] bare edit persist failed');
        }
        return synced;
    }, [
        syncBack,
        stageRef,
        scopeRef,
        quoteDataRef,
        quoteTitleRef,
        titleEditedByUserRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        projectAbortRef,
        setStageWithTransition,
        saveCurrentProject,
        isQuoteGenerationInFlight,
        invalidatePresentationEpoch,
        setPresentationQuoteData,
    ]);

    /** Hard reset for resetForm / sign-out / project-switch. Does NOT sync
     *  back — caller is intentionally throwing away modal state. Per
     *  DEC-COUNCIL-013 Kakashi F2 — isEditing resets to FALSE (matches the
     *  open-path default; the pre-hook `true` was vestigial). */
    const reset = useCallback(() => {
        invalidatePresentationEpoch();
        openRef.current = false;
        setIsOpen(false);
        setViewMode(false);
        setIsEditing(false);
        setLiveSnapshot(null);
        setPresentationQuoteData(null);
        actionsRef.current = {};
        heroDisplayCache.bindingRef.current = null;
        heroDisplayCache.contextKeyRef.current = null;
        stage5TopologyBaseKeysRef.current = null;
        lastPublishedNavigationPresentationRef.current = null;
    }, [setLiveSnapshot, setPresentationQuoteData, invalidatePresentationEpoch, heroDisplayCache]);

    /** Leave QuoteMaker for another app page without discarding the current
     *  project's resolved hero or durable peer-publication receipt. A real
     *  project switch still calls reset() from completeProjectLoad. */
    const suspendForPageNavigation = useCallback(() => {
        invalidatePresentationEpoch();
        openRef.current = false;
        setIsOpen(false);
        setViewMode(false);
        setIsEditing(false);
        setLiveSnapshot(null);
        setPresentationQuoteData(null);
        actionsRef.current = {};
        stage5TopologyBaseKeysRef.current = null;
    }, [setLiveSnapshot, setPresentationQuoteData, invalidatePresentationEpoch]);

    /** Session-restore escape hatch — for sessionStorage hydration at
     *  App.jsx:1067-1071 that needs to restore flags without firing any
     *  opening side effects. */
    const restoreFromSession = useCallback(({ open: o, viewMode: vm, isEditing: ie } = {}) => {
        // Returning to QuoteMaker may replay a complete stored Stage 5 view
        // while replacement generation continues. Viewing is safe; the live
        // generation lock still prevents stale edit/save mutation.
        if (o === false) invalidatePresentationEpoch();
        if (typeof o === 'boolean') {
            openRef.current = o;
            if (o === true) {
                // The suspend that preceded this restore cleared the baseline, and the other open intents set it from the scope
                // they present. Do the same here, from the quote the modal will show (its presentation copy, else the parent's
                // quote). A row Stage 4 gained meanwhile is NOT in that quote, so the baseline and the live scope differ and
                // Back's deletion authority stays off: the new row is never read as a deletion.
                const presented = presentationQuoteDataRef.current?.scope ?? quoteDataRef?.current?.scope;
                const baseScope = Array.isArray(presented)
                    ? presented
                    : (Array.isArray(scopeRef?.current) ? scopeRef.current : []);
                stage5TopologyBaseKeysRef.current = baseScope.map(scopeMergeKeys);
                // The load this editor shows, as the other open intents record
                // it: without it every Stage 5 item edit was refused from
                // Stage 4 after a session restore (review round 2, 2026-10-23).
                openedForLoadRef.current = paintedLoadKey(paintedProjectLoadContextRef);
            }
            setIsOpen(o);
        }
        if (typeof vm === 'boolean') setViewMode(vm);
        if (typeof ie === 'boolean') setIsEditing(ie);
        return true;
    }, [invalidatePresentationEpoch, paintedProjectLoadContextRef, quoteDataRef, scopeRef]);

    // ───── Returned interface ────────────────────────────────────────────

    return {
        // Reactive state
        isOpen,
        viewMode,
        isEditing,
        liveSnapshot,
        presentationQuoteData,
        presentationPersisting,
        // Non-reactive accessors (per DEC-COUNCIL-013 Kakashi F1 —
        // liveSnapshotRef is for event-handler consumers that don't need
        // a re-render every time the modal pushes a keystroke)
        openRef,
        liveSnapshotRef,
        // The view-only presentation copy, updated in the same turn as
        // presentationQuoteData. A callback created in an earlier render (a
        // Stage 5 Regenerate completion) reads the copy App shows now here.
        presentationQuoteDataRef,
        actionsRef,
        heroDisplayCache,
        // Fine-grained setters (used by the modal's internal edit-mode toggle
        // + the nav-away paths that hand control back to the modal)
        setViewMode,
        setIsEditing,
        setLiveSnapshot,
        adoptCanonicalSave,
        // Open intents (pick by call-site semantics)
        openAfterPipeline,
        openForNavigation,
        openRecovered,
        peek,
        restore,
        // Close intents
        close,
        suspendForPageNavigation,
        reset,
        // Session restore
        restoreFromSession,
        // Sync without closing (for nav-away paths at App.jsx:11157, 11484, 12630)
        syncBack,
        syncForPageNavigation,
        // Stage 4 and Stage 5 as one set of items
        publishStage5Rows,
        resolveStage4RowsFromStage5,
    };
}
